import { useTranslation } from '@hbs-us/ui';
import { getRouteApi } from '@tanstack/react-router';
import { RouteSkeleton } from '../_ops/RouteSkeleton.js';
import { UsersView } from './UsersView.js';
import { useUsersQuery } from './useUsersQuery.js';

/**
 * The `/users` (People & Access) route component (a NEW route — never stubbed). Reads the
 * typed search params (`q`, `role`), fires the coarse-endpoint TanStack Query, and renders
 * the redesigned `UsersView` on success. Loading → a page-shaped `RouteSkeleton`; error →
 * announced error state with a retry that refetches (the cluster 3-state contract,
 * Shared 9).
 *
 * The server computes the directory + the role/active counts; this component never
 * re-aggregates and never sends a tenant id (SEC-11).
 */

const routeApi = getRouteApi('/users');

export function UsersRoute() {
  const { t } = useTranslation();
  const search = routeApi.useSearch();
  const query = useUsersQuery();

  if (query.isPending) {
    return <RouteSkeleton label={t('users:title')} rows={6} columns={4} filters={0} />;
  }

  if (query.isError) {
    return (
      <section
        role="alert"
        aria-live="assertive"
        className="flex h-full flex-col items-center justify-center gap-4 bg-background p-16 text-center"
      >
        <h1 className="font-display font-semibold text-2xl text-foreground">
          {t('users:error.heading')}
        </h1>
        <p className="max-w-sm text-muted-foreground text-sm">{t('users:error.body')}</p>
        <button
          type="button"
          onClick={() => void query.refetch()}
          className="inline-flex h-11 items-center justify-center rounded-md bg-secondary px-4 font-medium text-secondary-foreground text-sm ring-offset-background transition-colors hover:bg-secondary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {t('users:error.retry')}
        </button>
      </section>
    );
  }

  return <UsersView data={query.data} search={search} />;
}
